import { FC, ReactNode } from 'react';
import { FormProvider } from 'react-hook-form';
import {
  Card,
  Icon,
  IconButton,
  Stack,
  TextBody,
  TextCaption,
} from '@pypestream/design-system';

import { useKnowledgeAgentUpload } from '../../hooks';
import { TabTasks } from '../../components/table/supervisor/tab-tasks';
import { useSupervisorForm } from '../../components/table/supervisor/use-supervisor-form';
import {
  KnowledgeSearchNotice,
  KnowledgeSearchSkippedNote,
} from '../../components/knowledge-search/knowledge-search-notice';
import '../../components/modals/create-solution-modal/step-agent.scss';

type SupervisorForm = ReturnType<typeof useSupervisorForm>;

/** The Knowledge Agent's documents: what's attached, and adding more. */
const KnowledgeDocuments: FC<{ form: SupervisorForm; viewOnly: boolean }> = ({
  form,
  viewOnly,
}) => {
  const { uploadedFiles, addFile, removeFile } = form;
  const { customFileUpload, formatFileSize, getFileIcon } =
    useKnowledgeAgentUpload({
      addFile,
      testId: 'bobcat-agent-settings-docs-upload',
      disabled: viewOnly,
    });
  return (
    <>
      <KnowledgeSearchNotice inAgentsPanel viewOnly={viewOnly} />
      <KnowledgeSearchSkippedNote />
      {!viewOnly && customFileUpload}
      {uploadedFiles.length > 0 && (
        <div className="c-create-solution-agent__files-list c-create-solution-agent__files-list--panel">
          <Stack gutter="xsmall">
            {uploadedFiles.map((file) => (
              <Card
                key={file.id}
                size="small"
                shadow="none"
                className="c-create-solution-agent__file c-create-solution-agent__file--large"
              >
                <Icon
                  name={getFileIcon(file.name)}
                  className="c-create-solution-agent__file-icon"
                />
                <TextBody>{file.name}</TextBody>
                <TextCaption>
                  {file.file
                    ? 'Added · saves with Save'
                    : formatFileSize(file.size)}
                </TextCaption>
                {!viewOnly && (
                  <IconButton
                    size="small"
                    name="delete"
                    slot="hover-action"
                    aria-label={`Remove ${file.name}`}
                    onClick={() => removeFile(file.id)}
                  />
                )}
              </Card>
            ))}
          </Stack>
        </div>
      )}
    </>
  );
};

export interface AgentExtras {
  /** Tasks and their example phrases, for the Supervisor tab. */
  supervisor: ReactNode;
  /** The Knowledge Agent's documents, for its tab. */
  knowledge: ReactNode;
  dirty: boolean;
  saving: boolean;
  /** Saves tasks and documents the way the full configuration's Save does. */
  save: () => Promise<void>;
  reset: () => void;
}

/**
 * The parts of the full Supervisor configuration (`SupervisorPanel`) that
 * belong beside Agent settings: the tasks the Supervisor routes to, with the
 * example phrases that route there, and the Knowledge Agent's documents.
 * Same form and same save (`useSupervisorForm`), so both places agree.
 */
export const useAgentExtras = (viewOnly: boolean): AgentExtras => {
  const form = useSupervisorForm();
  const { tasksForm, uploadedFiles } = form;
  return {
    supervisor: (
      <FormProvider {...tasksForm}>
        <TabTasks viewOnly={viewOnly} />
      </FormProvider>
    ),
    knowledge: <KnowledgeDocuments form={form} viewOnly={viewOnly} />,
    dirty:
      tasksForm.formState.isDirty || uploadedFiles.some((file) => !!file.file),
    saving: form.isLoading,
    // Resolves once the save lands (or gives up), so a write after it in the
    // same Save builds on it.
    save: () =>
      new Promise<void>((resolve) => {
        const giveUp = setTimeout(resolve, 20_000);
        void form.saveSupervisor(() => {
          clearTimeout(giveUp);
          resolve();
        });
      }),
    reset: () => form.resetFormState(),
  };
};
